import { useId, useState } from "react";
import {
	resizeTripDates,
	swapTripDays,
	type TripWorkspace,
} from "@japangolmok/core";
import type { PersistTrip } from "./TripExperience";
import { BottomSheet } from "../ui/BottomSheet";
export function TripEditor({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [open, setOpen] = useState(false),
		[notice, setNotice] = useState("");
	return (
		<>
			<button
				className="button secondary"
				type="button"
				onClick={() => setOpen(true)}
			>
				여행 정보 고치기
			</button>
			{open && (
				<BottomSheet title="여행 정보 고치기" onClose={() => setOpen(false)}>
					<form
						className="compact-form"
						onSubmit={async (e) => {
							e.preventDefault();
							const d = new FormData(e.currentTarget);
							try {
								const size = Number(d.get("partySize"));
								if (
									size < trip.partySize &&
									!window.confirm(
										"인원을 줄이면 제외되는 별명의 지출과 담당은 나에게 옮겨요. 계속할까요?",
									)
								)
									return;
								const ok = await persist((draft) => {
									const resized = resizeTripDates(
										draft,
										String(d.get("start")),
										String(d.get("end")),
									);
									Object.assign(draft, resized);
									draft.title = String(d.get("title")).trim();
									draft.partySize = size;
									draft.dailyBudgetJpy = Number(d.get("budget"));
									draft.krwPer100Jpy = Number(d.get("rate")) || null;
									const removed =
										draft.party?.slice(size).map((p) => p.id) ?? [];
									draft.party = draft.party?.slice(0, size);
									const me = draft.party?.[0]?.id ?? "me";
									draft.expenses.forEach((x) => {
										if (removed.includes(x.paidBy ?? "")) x.paidBy = me;
										x.splitWith = x.splitWith
											?.map((id) => (removed.includes(id) ? me : id))
											.filter((id, i, a) => a.indexOf(id) === i);
									});
									draft.checklist.forEach((x) => {
										if (removed.includes(x.assignee ?? "")) x.assignee = me;
									});
								});
								if (ok !== false) {
									setOpen(false);
									setNotice(
										"여행 정보를 저장했어요. 날짜를 바꿨다면 캘린더를 다시 넣어 주세요.",
									);
								}
							} catch {
								setNotice(
									"날짜와 입력을 확인해 주세요. 여행은 1~31일이에요. 원래 여행은 그대로예요.",
								);
							}
						}}
					>
						<label>
							여행 이름
							<input
								name="title"
								defaultValue={trip.title}
								maxLength={80}
								required
							/>
						</label>
						<label>
							가는 날
							<input
								type="date"
								name="start"
								defaultValue={trip.startDate}
								required
							/>
						</label>
						<label>
							오는 날
							<input
								type="date"
								name="end"
								defaultValue={trip.endDate}
								required
							/>
						</label>
						<label>
							인원
							<input
								type="number"
								name="partySize"
								min={1}
								max={12}
								defaultValue={trip.partySize}
								required
							/>
						</label>
						<label>
							1인 하루 예산 · 엔
							<input
								type="number"
								name="budget"
								min={0}
								max={1000000}
								defaultValue={trip.dailyBudgetJpy}
								required
							/>
						</label>
						<label>
							내 환율 · 100엔당 원
							<input
								type="number"
								name="rate"
								min={100}
								max={10000}
								defaultValue={trip.krwPer100Jpy ?? ""}
							/>
						</label>
						<button type="submit" className="button primary">
							여행 정보 저장
						</button>
					</form>
					{notice && <p role="alert">{notice}</p>}
				</BottomSheet>
			)}
			{!open && notice && <p role="status">{notice}</p>}
		</>
	);
}
export function DaySwap({
	trip,
	date,
	persist,
}: {
	trip: TripWorkspace;
	date: string;
	persist: PersistTrip;
}) {
	const [open, setOpen] = useState(false);
	const panelId = useId();
	const [other, setOther] = useState(
			trip.days.find((d) => d.date !== date)?.date ?? date,
		),
		[before, setBefore] = useState<{
			trip: TripWorkspace;
			after: string;
		} | null>(null),
		[notice, setNotice] = useState("");
	return (
		<div className="day-control">
			<button type="button" className="button secondary" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(!open)}><span aria-hidden="true">⇄</span> 다른 날과 바꾸기</button>
			<div id={panelId} hidden={!open}>
			<label>
				바꿀 날
				<select value={other} onChange={(e) => setOther(e.target.value)}>
					{trip.days
						.filter((d) => d.date !== date)
						.map((d) => (
							<option key={d.date}>{d.date}</option>
						))}
				</select>
			</label>
			<button
				type="button"
				className="button secondary"
				disabled={other === date}
				onClick={async () => {
					const old = structuredClone(trip);
					const ok = await persist((d) =>
						Object.assign(d, swapTripDays(d, date, other)),
					);
					if (ok !== false)
						setBefore({
							trip: old,
							after: JSON.stringify(swapTripDays(old, date, other).days),
						});
				}}
			>
				두 날 일정 바꾸기
			</button>
			{before && (
				<button
					type="button"
					className="button text"
					onClick={async () => {
						if (JSON.stringify(trip.days) !== before.after) {
							setNotice(
								"그동안 일정이 달라졌어요. 현재 일정을 확인한 뒤 두 날을 다시 바꿔 주세요.",
							);
							return;
						}
						const ok = await persist((d) => {
							if (JSON.stringify(d.days) !== before.after)
								throw new Error("STALE_DAY_SWAP");
							d.days = before.trip.days;
							d.filmMoments = before.trip.filmMoments;
							d.lastAdjustment = null;
							d.lastReplacement = null;
						});
						if (ok !== false) setBefore(null);
					}}
				>
					두 날 바꾸기 되돌리기
				</button>
			)}
			{notice && <p role="status">{notice}</p>}
			</div>
		</div>
	);
}
